import { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/compat/router';

import { getPersistedValue } from 'shared/utils/persisted-values';
import { useOrderForConfirmation } from 'src/checkout/hooks/use-order-for-confirmation';
import { CreateInstoreCartUrlDocument } from 'src/gql/graphql';
import { PBB_REOPEN_PENDING_KEY } from 'src/components/modals/order-status-modal/v2/pbb-return-storage';
import { PBB_PAYMENT_SWITCH_FLAG, useLatchedFlag } from 'src/payments/dutchie-pay/flags';
import { buildInstoreCartPath } from 'src/payments/utils/instore-cart-path';
import { getLogger } from 'src/utils/datadog-logger';

const logger = getLogger('use-finalize-pbb-after-signup');

// If the order data needed to hand off to /pay/instore can't be loaded within this window,
// the hook gives up and the consumer falls back to the legacy `router.push(refUrl)` path.
const FINALIZE_TIMEOUT_MS = 5_000;

type UseFinalizePbbAfterSignupReturn = {
  shouldFinalize: boolean;
  isReady: boolean;
  /**
   * True when we wanted to finalize but can't — order load errored, query was skipped (no
   * userId), or `FINALIZE_TIMEOUT_MS` elapsed before the order resolved. Consumer should
   * stop waiting and fall back to its legacy redirect path.
   */
  hasFailed: boolean;
  finalizeAndRedirect: (fallbackRefUrl: string) => Promise<void>;
};

/**
 * Post-enrollment hand-off for shoppers who started PBB from the order confirmation modal.
 * When `pbbReopenPending` + `viewOrderId` are in sessionStorage, mint the instore cart URL
 * and send them straight to `/pay/instore` instead of letting `/pay/signup` redirect to
 * `refUrl` first. Reads `viewOrderId` from sessionStorage so it survives Plaid OAuth hard
 * reloads. Flag-off falls through to the default `router.push(refUrl)`.
 */
export function useFinalizePbbAfterSignup(): UseFinalizePbbAfterSignupReturn {
  // Latched so an LD identify-flicker mid-enrollment can't race the redirect effect and
  // fall through to `router.push(refUrl)` (see critical-patterns #1).
  const flagOn = useLatchedFlag(PBB_PAYMENT_SWITCH_FLAG);
  const router = useRouter();
  const persistedViewOrderId = getPersistedValue('viewOrderId');
  const viewOrderId = typeof persistedViewOrderId === 'string' ? persistedViewOrderId : '';

  const reopenPending = getPersistedValue(PBB_REOPEN_PENDING_KEY) === true;
  const shouldFinalize = flagOn && reopenPending && Boolean(viewOrderId);

  const { order, error: orderError } = useOrderForConfirmation({
    orderId: shouldFinalize ? viewOrderId : '',
  });

  const [createInstoreCartUrl] = useMutation(CreateInstoreCartUrlDocument);

  // viewOrderId is human-readable; the mutation needs the Mongo ObjectId (`order.id`).
  const orderObjectId = order?.id ?? '';
  const updateOrderToken = order?.updateOrderToken ?? '';

  const isReady = shouldFinalize && Boolean(order) && Boolean(orderObjectId) && Boolean(updateOrderToken);

  const [hasTimedOut, setHasTimedOut] = useState(false);
  useEffect(() => {
    if (!shouldFinalize || isReady || hasTimedOut) {
      return undefined;
    }
    const id = setTimeout(() => setHasTimedOut(true), FINALIZE_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [shouldFinalize, isReady, hasTimedOut]);

  const hasFailed = shouldFinalize && !isReady && (orderError !== null || hasTimedOut);
  useEffect(() => {
    if (!hasFailed) {
      return;
    }
    logger.error('PBB post-signup finalize gave up before order resolved', {
      viewOrderId,
      timedOut: hasTimedOut,
      orderErrored: orderError !== null,
    });
  }, [hasFailed, viewOrderId, hasTimedOut, orderError]);

  const finalizeAndRedirect = useCallback(
    async (fallbackRefUrl: string): Promise<void> => {
      try {
        const cartResult = await createInstoreCartUrl({ variables: { orderId: orderObjectId } });
        const rawUrl = cartResult.data?.createInstoreCartUrl?.instoreCartUrl;
        if (rawUrl) {
          const path = buildInstoreCartPath(rawUrl, {
            orderId: orderObjectId,
            updateOrderToken,
            refUrl: fallbackRefUrl,
          });
          void router?.push(path);
          return;
        }
        void router?.push(fallbackRefUrl);
      } catch (err) {
        logger.error('Failed to fetch instore cart URL after signup', {
          orderId: orderObjectId,
          error: (err as Error).message,
        });
        void router?.push(fallbackRefUrl);
      }
    },
    [router, createInstoreCartUrl, orderObjectId, updateOrderToken]
  );

  return { shouldFinalize, isReady, hasFailed, finalizeAndRedirect };
}
